import { CommonModule } from '@angular/common';
import { Component, OnInit, Signal, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../../../core/services/auth.service';
import { InvitationService, InvitationViewModel } from '../../../core/services/invitation.service';
import { LoggerService } from '../../../core/services/logger.service';
import { OrganizationUser, UserService } from '../../../core/services/user.service';
import { BackButtonComponent } from '../../../shared/components/back-button/back-button.component';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { CopyButtonComponent } from '../../../shared/components/copy-button/copy-button.component';
import { CheckboxComponent } from '../../../shared/components/form-controls/checkbox/checkbox.component';
import { InputComponent } from '../../../shared/components/form-controls/input/input.component';
import { SelectComponent } from '../../../shared/components/form-controls/select/select.component';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { ModalDialogComponent } from '../../../shared/components/modal-dialog/modal-dialog.component';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';

@Component({
  selector: 'app-invitations-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    BackButtonComponent,
    ButtonComponent,
    CheckboxComponent,
    CopyButtonComponent,
    IconComponent,
    SelectComponent,
    InputComponent,
    ModalDialogComponent,
    TranslatePipe,
  ],
  templateUrl: './invitations-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './invitations-list.component.css',
})
export class InvitationsListComponent implements OnInit {
  private invitationService = inject(InvitationService);
  private authService = inject(AuthService);
  private userService = inject(UserService);
  private logger = inject(LoggerService).getLogger('invitations');

  // RQS pattern for invitations
  invitations!: Signal<InvitationViewModel[]>;
  loading!: Signal<boolean>;
  error!: Signal<string | null>;

  // RQS pattern for users
  users!: Signal<OrganizationUser[]>;
  usersLoading!: Signal<boolean>;
  usersError!: Signal<string | null>;

  // Form state
  email = '';
  role: 'admin' | 'user' = 'user';
  creating = false;

  // Cancel confirmation modal
  showCancelModal = false;
  invitationToCancel: { id: string; email: string } | null = null;

  // Error display
  actionError: string | null = null;

  // Not-implemented dialog
  showNotImplementedModal = false;

  // Show inactive users toggle
  showInactiveUsers = false;

  // Remove/leave confirmation modal
  showRemoveModal = false;
  userToRemove: { id: string; name: string; isCurrentUser: boolean } | null = null;

  // Pagination
  displayCount = 10;

  get displayedInvitations(): InvitationViewModel[] {
    return this.invitations().slice(0, this.displayCount);
  }

  get hasMoreInvitations(): boolean {
    return this.invitations().length > this.displayCount;
  }

  showMore() {
    this.displayCount += 10;
  }

  async ngOnInit() {
    const query = this.invitationService.createQuery();
    this.invitations = query.data;
    this.loading = query.loading;
    this.error = query.error;

    this.refreshUsers();
  }

  private refreshUsers() {
    const usersQuery = this.userService.createQuery(this.showInactiveUsers);
    this.users = usersQuery.data;
    this.usersLoading = usersQuery.loading;
    this.usersError = usersQuery.error;
  }

  onShowInactiveChange() {
    this.refreshUsers();
  }

  // Get current user's ID for comparison (to show "You" label)
  get currentUserId(): string | null {
    const user = this.authService.getCurrentUser();
    return user?.id || null;
  }

  // Placeholder methods
  showChangeRoleDialog(member: any) {
    this.showNotImplementedModal = true;
  }

  showRemoveMemberDialog(member: OrganizationUser) {
    const isCurrentUser = member.id === this.currentUserId;
    this.userToRemove = {
      id: member.id,
      name: member.full_name || member.email,
      isCurrentUser,
    };
    this.showRemoveModal = true;
  }

  async confirmRemoveMember() {
    if (!this.userToRemove) return;

    try {
      await this.userService.deactivate(this.userToRemove.id);

      // If removing yourself, logout and redirect
      if (this.userToRemove.isCurrentUser) {
        this.authService.logout();
      }

      this.showRemoveModal = false;
      this.userToRemove = null;
      this.refreshUsers();
    } catch (error) {
      this.actionError = 'Failed to remove member. Please try again.';
      this.logger.error('Remove member error:', error);
    }
  }

  cancelRemove() {
    this.showRemoveModal = false;
    this.userToRemove = null;
  }

  closeNotImplementedModal() {
    this.showNotImplementedModal = false;
  }

  async createInvitation() {
    if (!this.email || this.creating) return;

    this.creating = true;
    try {
      const user = this.authService.getCurrentUser();
      if (!user) throw new Error('Not authenticated');

      // Get customer_id from JWT token
      const token = this.authService.getToken();
      if (!token) throw new Error('No auth token');
      const payload = this.authService.decodeJwt(token);

      await this.invitationService.create(this.email, this.role, user.id, payload.customer_id);

      // Reset form
      this.email = '';
      this.role = 'user';
    } catch (err) {
      this.logger.error('Failed to create invitation:', err);
      this.actionError = 'Failed to create invitation. Please try again.';
    } finally {
      this.creating = false;
    }
  }

  showCancelConfirmation(invitation: InvitationViewModel) {
    const data =
      typeof invitation.data === 'string' ? JSON.parse(invitation.data) : invitation.data;
    this.invitationToCancel = { id: invitation.id, email: data.email };
    this.showCancelModal = true;
  }

  closeCancelModal() {
    this.showCancelModal = false;
    this.invitationToCancel = null;
  }

  async confirmCancel() {
    if (!this.invitationToCancel) return;

    try {
      this.showCancelModal = false;
      const user = this.authService.getCurrentUser();
      if (!user) throw new Error('Not authenticated');

      await this.invitationService.cancel(this.invitationToCancel.id, user.id);
    } catch (err) {
      this.logger.error('Failed to cancel invitation:', err);
      this.actionError = 'Failed to cancel invitation. Please try again.';
    } finally {
      this.invitationToCancel = null;
    }
  }

  closeErrorMessage() {
    this.actionError = null;
  }

  getInvitationUrl(token: string): string {
    return this.invitationService.getInvitationUrl(token);
  }
}
